import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import { ShowErrorViewModel } from "../../../commons/scaffold/scaffold";
import { MaterialType } from "../../commons/enums";
import { MasterItemModel } from "../../commons/master_item_model";
import GetMenuPrepBomDetailResponse from "../models/get_menu_preparation_bom_detail_response";
import { MenuPrepBomModel } from "../models/menu_preparation_bom_model";
import { SubmitMenuPreparationBomRequest } from "../models/submit_menu_preparation_bom_request";

export default class MenuPreparationBomDashboardViewModel extends ProviderVM {
    isLoading: boolean = false;
    errorModel?: ShowErrorViewModel;

    searchKeyword: string = "";

    resultItemList: MasterItemModel[] = [];
    menuItemOptions: MasterItemModel[] = [];
    mprItemOptions: MasterItemModel[] = [];

    readOnlyMprMap: Map<number, MenuPrepBomModel> = new Map<
        number,
        MenuPrepBomModel
    >();
    createMprMap: Map<number, MenuPrepBomModel> = new Map<
        number,
        MenuPrepBomModel
    >();

    isReadOnlyFormOpen: boolean = false;
    readOnlyFormItemId?: number;
    readOnlyFormItemName: string = "";
    readOnlyFormItemMaterialCode: string = "";

    isCreateFormOpen: boolean = false;
    createFormItemId?: number;
    createFormItemName: string = "";
    createFormItemMaterialCode: string = "";
    createFormSelectedMprId?: number;

    constructor() {
        super();
        this.fetchResultList();
        this.fetchMenuItemOptions();
        this.fetchMprItemOptions();
    }

    updateSearchKeyword(value: string) {
        this.emit(() => {
            this.searchKeyword = value;
        });
    }

    getFilteredResultList() {
        if (this.searchKeyword.length === 0) {
            return this.resultItemList;
        }

        const filteredResultList = this.resultItemList.filter((item) =>
            item.item
                .toLocaleLowerCase()
                .includes(this.searchKeyword.toLocaleLowerCase())
        );

        return filteredResultList;
    }

    fetchResultList() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.getMenuPrepBomMenuList, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<MasterItemModel[]>) => {
                const resultItemList = response.data as MasterItemModel[];
                this.emit(() => {
                    this.resultItemList = resultItemList;
                    this.isLoading = false;
                });
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: error,
                        action: () => this.fetchResultList(),
                    };
                });
            },
        });
    }

    fetchMenuItemOptions() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetchWithQueryParams(Endpoints.getMenuPrepBomItemOptions, {
            httpMethod: HttpMethod.get,
            queryParameters: {
                material_type: MaterialType.MENU,
            },
            onSuccess: (response: BaseResponse<MasterItemModel[]>) => {
                const resultItemOptions = response.data as MasterItemModel[];
                this.emit(() => {
                    this.isLoading = false;
                    this.menuItemOptions = resultItemOptions;
                });
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: error,
                        action: () => this.fetchMenuItemOptions(),
                    };
                });
            },
        });
    }

    fetchMprItemOptions() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetchWithQueryParams(Endpoints.getMenuPrepBomItemOptions, {
            httpMethod: HttpMethod.get,
            queryParameters: {
                material_type: MaterialType.MENU_PREPARE,
            },
            onSuccess: (response: BaseResponse<MasterItemModel[]>) => {
                const mprItemOptions = response.data as MasterItemModel[];
                this.emit(() => {
                    this.isLoading = false;
                    this.mprItemOptions = mprItemOptions;
                });
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: error,
                        action: () => this.fetchMprItemOptions(),
                    };
                });
            },
        });
    }

    fetchMprDetail(itemId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.getMenuPrepBomDetail}/${itemId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<GetMenuPrepBomDetailResponse>
                ) => {
                    const menuPrepBomResponse =
                        response.data as GetMenuPrepBomDetailResponse;

                    const mprMap = new Map<number, MenuPrepBomModel>();

                    menuPrepBomResponse.materials.forEach((material) => {
                        mprMap.set(material.mprId, material);
                    });

                    this.emit(() => {
                        this.isLoading = false;
                        this.readOnlyFormItemName =
                            menuPrepBomResponse.itemName;
                        this.readOnlyFormItemMaterialCode =
                            menuPrepBomResponse.itemMaterialCode;
                        this.readOnlyMprMap = mprMap;
                    });
                },
                onFailure: (error) => {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            message: error,
                            action: () => this.fetchMprDetail(itemId),
                        };
                    });
                },
            }
        );
    }

    submitMenuPrepBom() {
        if (this.createFormItemId) {
            this.emit(() => {
                this.isLoading = true;
            });

            const submitParams: SubmitMenuPreparationBomRequest = {
                itemId: this.createFormItemId,
                materials: [...this.createMprMap.values()].map((value) => ({
                    mprId: value.mprId,
                    mprQty: value.mprQty,
                })),
            };

            ApiService.fetch(Endpoints.createMenuPrepBom, {
                httpMethod: HttpMethod.post,
                parameters: submitParams,
                onSuccess: (response: BaseResponse<{}>) => {
                    this.emit(() => {
                        this.isLoading = false;
                    });
                    this.resetCreateForm();
                    this.fetchResultList();
                    Swal.fire({
                        title: "Berhasil",
                        icon: "success",
                    });
                },
                onFailure: (error) => {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            message: error,
                            action: () => {},
                        };
                    });
                },
            });
        }
    }

    openReadOnlyForm() {
        this.emit(() => {
            this.isReadOnlyFormOpen = true;
        });
    }

    resetReadOnlyForm() {
        this.emit(() => {
            this.readOnlyFormItemId = undefined;
            this.readOnlyFormItemName = "";
            this.readOnlyFormItemMaterialCode = "";
            this.readOnlyMprMap = new Map<number, MenuPrepBomModel>();
            this.isReadOnlyFormOpen = false;
        });
    }

    openCreateForm() {
        this.emit(() => {
            this.isCreateFormOpen = true;
        });
    }

    resetCreateForm() {
        this.emit(() => {
            this.createFormItemId = undefined;
            this.createFormItemName = "";
            this.createFormItemMaterialCode = "";
            this.createMprMap = new Map<number, MenuPrepBomModel>();
            this.isCreateFormOpen = false;
        });
    }

    updateResultItemId(itemId: number) {
        this.emit(() => {
            this.createFormItemId = itemId;
        });
    }

    updateSelectedMprId(mprId: number) {
        this.emit(() => {
            this.createFormSelectedMprId = mprId;
        });
    }

    handleAddMpr(itemId: number) {
        const updatedMap = new Map(this.createMprMap);

        const selectedItem = this.mprItemOptions.find(
            (item) => item.id === itemId
        );

        if (selectedItem) {
            updatedMap.set(itemId, {
                mprId: selectedItem.id,
                mprMaterialCode: selectedItem.materialCode,
                mprName: selectedItem.item,
                mprQty: 1,
            });
        }

        this.emit(() => {
            this.createMprMap = updatedMap;
        });
    }

    handleUpdateMprQty(itemId: number, qty: number) {
        const updatedMap = new Map(this.createMprMap);

        const selectedItem = this.mprItemOptions.find(
            (item) => item.id === itemId
        );

        if (selectedItem) {
            updatedMap.set(itemId, {
                mprId: selectedItem.id,
                mprMaterialCode: selectedItem.materialCode,
                mprName: selectedItem.item,
                mprQty: qty,
            });
        }

        this.emit(() => {
            this.createMprMap = updatedMap;
        });
    }
}
